// Önerilen Dağıtım Algoritması

import { turkishToLower } from './stringUtils';
import { shuffle } from './arrayUtils';

const EPS = 1e-9;

const normalizeAgirliklar = (settings) => {
    const p = Math.max(0, Number(settings?.puanAgirligi) || 0.7);
    const a = Math.max(0, Number(settings?.adetAgirligi) || 0.3);
    const c = Math.max(0, Number(settings?.cesitlilikAgirligi) || 0);
    const t = p + a + c;
    if (t <= EPS) return { puanW: 0.5, adetW: 0.5, cesitlilikW: 0 };
    return { puanW: p / t, adetW: a / t, cesitlilikW: c / t };
};

/**
 * Bir hakim için kare normalize sapma maliyeti.
 * - devPuan: toplam puan hedef sapması
 * - devAdet: dosya adedi hedef sapması
 * - devOrtPuan: hakim başına ORTALAMA DOSYA PUANI hedef sapması — bu, salt toplam
 *   eşitliğine değil, zor-kolay karışım (çeşitlilik) hedefine de baskı uygular;
 *   böylece bir hakime sadece zor veya sadece kolay dosya yığılması cezalanır.
 * Hedef 0 iken mevcut > 0 ise outlier gibi davranır (aşırı büyük maliyet).
 */
const quadDevCost = (puan, adet, hedefPuan, hedefAdet, puanW, adetW, cesitlilikW = 0) => {
    const devPuan = hedefPuan > EPS
        ? (puan - hedefPuan) / hedefPuan
        : (puan > EPS ? 1e6 : 0);
    const devAdet = hedefAdet > EPS
        ? (adet - hedefAdet) / hedefAdet
        : (adet > EPS ? 1e6 : 0);

    let devOrtPuanSq = 0;
    if (cesitlilikW > EPS && hedefAdet > EPS && hedefPuan > EPS) {
        const hedefOrtPuan = hedefPuan / hedefAdet;
        const mevcutOrtPuan = adet > EPS ? puan / adet : hedefOrtPuan;
        const devOrtPuan = (mevcutOrtPuan - hedefOrtPuan) / hedefOrtPuan;
        devOrtPuanSq = devOrtPuan * devOrtPuan;
    }

    return devPuan * devPuan * puanW + devAdet * devAdet * adetW + devOrtPuanSq * cesitlilikW;
};

/** Aynı puana sahip dosyaları gruplayıp Fisher–Yates ile karıştırır (deterministik sıra kırılır). */
const sortDosyalarByPuanDescShuffleTies = (dosyalar) => {
    const sorted = [...dosyalar].sort((a, b) => (b.dosyaPuani || 0) - (a.dosyaPuani || 0));
    const out = [];
    let i = 0;
    while (i < sorted.length) {
        const p = sorted[i].dosyaPuani;
        let j = i + 1;
        while (j < sorted.length && sorted[j].dosyaPuani === p) j += 1;
        out.push(...shuffle(sorted.slice(i, j)));
        i = j;
    }
    return out;
};

export const distributeDosyalar = (dosyalar, hakimler, settings) => {
    const aktifHakimler = hakimler.filter((h) => h.isAvailableForAssignment);
    if (aktifHakimler.length === 0) {
        return createEmptyResult(dosyalar.length);
    }

    const siraliDosyalar = sortDosyalarByPuanDescShuffleTies(dosyalar);
    const toplamYeniDosyaSayisi = siraliDosyalar.length;
    const toplamYeniDosyaHamPuani = siraliDosyalar.reduce((sum, dosya) => sum + (dosya.dosyaPuani || 0), 0);
    const toplamTevziOrani = aktifHakimler.reduce((sum, hakim) => sum + parseAssignmentRatio(hakim.assignmentRatio), 0);

    let mevcutToplamPuan = 0;
    let mevcutToplamDosyaSayisi = 0;

    const hakimPuanlari = new Map();
    const hakimDosyaSayilari = new Map();
    const hakimDosyalari = new Map(aktifHakimler.map((h) => [h.id, []]));

    aktifHakimler.forEach((h) => {
        const hScore = h.score || 0;
        const hCount = h.dosyaSayisi ?? 0;
        hakimPuanlari.set(h.id, hScore);
        hakimDosyaSayilari.set(h.id, hCount);
        mevcutToplamPuan += hScore;
        mevcutToplamDosyaSayisi += hCount;
    });

    // Phase 1 ve Phase 2'de atanan dosyaların id'leri: yerel iyileştirme bunları taşımaz.
    const pinnedIds = new Set();

    // İlk aşama: Eski hakimlere dosyaları ata (sert kural — sabitlenir).
    // assignDosyaToHakim eski hakim çarpanını uygular; hakim puanına yansıyan
    // efektif yük buradan hakimPuanlari üzerinden okunabilir.
    let kalanDosyalar = siraliDosyalar.filter((dosya) => {
        const eskiHakimler = findEskiHakimler(dosya.eskiHakim, aktifHakimler);
        if (eskiHakimler.length > 0) {
            const eskiHakim = eskiHakimler[0];
            assignDosyaToHakim(eskiHakim, dosya, hakimPuanlari, hakimDosyalari, hakimDosyaSayilari, settings, true);
            pinnedIds.add(dosya.id);
            return false;
        }
        return true;
    });

    // İkinci aşama: Manuel tevzi (sert kural — sabitlenir).
    kalanDosyalar = kalanDosyalar.filter((dosya) => {
        if (dosya.manuelTevziEdilecekHakim) {
            const manuelHakim = findHakimByName(dosya.manuelTevziEdilecekHakim, aktifHakimler);
            if (manuelHakim) {
                assignDosyaToHakim(manuelHakim, dosya, hakimPuanlari, hakimDosyalari, hakimDosyaSayilari, settings, false);
                pinnedIds.add(dosya.id);
                return false;
            }
        }
        return true;
    });

    // ŞİMDİ hedef hesaplanır: eski hakim ataması olan dosyaların efektif yükü
    // (carpan uygulanmış) ve manuel tevzi yükleri zaten hakimPuanlari'ya işlendi.
    // Toplam mutlak puan = mevcut baseline + sabit atamaların efektif katkısı + kalan ham puan.
    const kalanDosyalarHamPuan = kalanDosyalar.reduce((sum, d) => sum + (d.dosyaPuani || 0), 0);
    const sabitAtamalarinEfektifKatkisi = Array.from(hakimPuanlari.values()).reduce((s, v) => s + v, 0) - mevcutToplamPuan;
    const mutlakToplamPuan = mevcutToplamPuan + sabitAtamalarinEfektifKatkisi + kalanDosyalarHamPuan;
    const mutlakToplamDosyaSayisi = mevcutToplamDosyaSayisi + toplamYeniDosyaSayisi;

    const birimHedefPuan = toplamTevziOrani > EPS ? mutlakToplamPuan / toplamTevziOrani : 0;
    const birimHedefDosyaSayisi = toplamTevziOrani > EPS ? mutlakToplamDosyaSayisi / toplamTevziOrani : 0;

    const hakimHedefPuanlari = new Map();
    const hakimHedefDosyaSayilari = new Map();

    aktifHakimler.forEach((h) => {
        const tevziOrani = parseAssignmentRatio(h.assignmentRatio);
        hakimHedefPuanlari.set(h.id, birimHedefPuan * tevziOrani);
        hakimHedefDosyaSayilari.set(h.id, birimHedefDosyaSayisi * tevziOrani);
    });

    const { puanW, adetW, cesitlilikW } = normalizeAgirliklar(settings);

    // Üçüncü aşama: Bantlı round-robin atama.
    // Mevcut maliyet bazlı greedy "hedefe yakın" baskısı nedeniyle aynı puanlı
    // dosyaları aynı hakime yığma eğilimindeydi (5 tane 30-puanlık dosyanın 2-3'ü
    // tek hakime gidebilir). Bunun yerine: aynı puanlı dosyaları bir grup say,
    // her grupta hakimleri (oran düzeltilmiş) yüke göre artan sırala ve dosyaları
    // round-robin ata. Böylece her bant zorla farklı hakimlere dağılır; yerel
    // iyileştirme arkasından hedef sapmasını rafine eder.
    let dosyaIdx = 0;
    while (dosyaIdx < kalanDosyalar.length) {
        const grupPuani = kalanDosyalar[dosyaIdx].dosyaPuani;
        let grupSon = dosyaIdx + 1;
        while (grupSon < kalanDosyalar.length && kalanDosyalar[grupSon].dosyaPuani === grupPuani) {
            grupSon += 1;
        }
        const grup = kalanDosyalar.slice(dosyaIdx, grupSon);

        // Hakimleri oran-düzeltilmiş yüke göre artan sırala; eşit yüklerde
        // shuffle ile bias kırılır.
        const sortedHakimler = shuffle([...aktifHakimler]).sort((a, b) => {
            const oranA = parseAssignmentRatio(a.assignmentRatio) || 1;
            const oranB = parseAssignmentRatio(b.assignmentRatio) || 1;
            const yukA = (hakimPuanlari.get(a.id) ?? 0) / oranA;
            const yukB = (hakimPuanlari.get(b.id) ?? 0) / oranB;
            return yukA - yukB;
        });

        grup.forEach((dosya, idx) => {
            const hakim = sortedHakimler[idx % sortedHakimler.length];
            assignDosyaToHakim(hakim, dosya, hakimPuanlari, hakimDosyalari, hakimDosyaSayilari, settings, false);
        });

        dosyaIdx = grupSon;
    }

    // Dördüncü aşama: Yerel iyileştirme (2-swap + 1-way move) — sadece sabitlenmemiş dosyalar.
    balanceBySwaps({
        aktifHakimler,
        hakimPuanlari,
        hakimDosyaSayilari,
        hakimDosyalari,
        hakimHedefPuanlari,
        hakimHedefDosyaSayilari,
        pinnedIds,
        puanW,
        adetW,
        cesitlilikW,
    });

    return {
        dagitilmisDosyalar: Array.from(hakimDosyalari.values()).flat(),
        yeniHakimPuanlari: Object.fromEntries(hakimPuanlari),
        hakimHedefPuanlari: Object.fromEntries(hakimHedefPuanlari),
        hakimDosyaSayilari: Object.fromEntries(hakimDosyaSayilari),
        toplamDosyaPuani: toplamYeniDosyaHamPuani,
        ortalamaHakimPuani: birimHedefPuan,
        dengesizlikOrani: calculateDengesizlikOrani(hakimPuanlari, hakimHedefPuanlari)
    };
};

const findEskiHakimler = (eskiHakimBilgisi, hakimler) => {
    if (!eskiHakimBilgisi) return [];

    // Trim + Türkçe küçük harf: Excel'den gelen "  Hakim Ayşe " ve listede
    // "Hakim Ayşe" eşleşsin.
    const eskiHakimLower = turkishToLower(String(eskiHakimBilgisi).trim());
    const hakimNames = hakimler.map((h) => turkishToLower(String(h.name || '').trim()));
    const pattern = hakimNames.filter(Boolean).map(escapeRegExp).join('|');
    if (!pattern) return [];
    const regex = new RegExp(pattern, 'g');

    const matches = eskiHakimLower.match(regex) || [];
    return hakimler.filter((h) => matches.includes(turkishToLower(String(h.name || '').trim())));
};

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const assignDosyaToHakim = (hakim, dosya, hakimPuanlari, hakimDosyalari, hakimDosyaSayilari, settings, isEskiHakim) => {
    let atanacakPuan;

    const hamPuan = dosya.dosyaPuani || 0;
    if (isEskiHakim) {
        atanacakPuan = hamPuan * settings.hakimKendiDosyasiCarpani;
    } else {
        atanacakPuan = hamPuan;
    }

    hakimPuanlari.set(hakim.id, (hakimPuanlari.get(hakim.id) ?? 0) + atanacakPuan);
    hakimDosyalari.get(hakim.id).push({ ...dosya, atananHakim: hakim.name });
    hakimDosyaSayilari.set(hakim.id, hakimDosyaSayilari.get(hakim.id) + 1);
};

/**
 * Dağıtım sonrası yerel iyileştirme:
 *  - 1-way move: h1'den h2'ye bir serbest dosyayı taşı.
 *  - 2-way swap: h1 ve h2'deki iki serbest dosyayı takas et.
 * Her turda tüm adayları tarar, kare normalize sapma maliyetini en çok azaltanı uygular.
 * pinnedIds içindeki dosyalar (eski hakim veya manuel tevzi ile sabitlenmiş) taşınmaz.
 */
const balanceBySwaps = ({
    aktifHakimler,
    hakimPuanlari,
    hakimDosyaSayilari,
    hakimDosyalari,
    hakimHedefPuanlari,
    hakimHedefDosyaSayilari,
    pinnedIds,
    puanW,
    adetW,
    cesitlilikW,
}) => {
    const MAX_ITERS = 50;
    const MIN_IMPROVEMENT = 1e-9;

    const hakimById = new Map(aktifHakimler.map((h) => [h.id, h]));

    const costFor = (hakimId) => quadDevCost(
        hakimPuanlari.get(hakimId) ?? 0,
        hakimDosyaSayilari.get(hakimId) ?? 0,
        hakimHedefPuanlari.get(hakimId) ?? 0,
        hakimHedefDosyaSayilari.get(hakimId) ?? 0,
        puanW,
        adetW,
        cesitlilikW
    );

    for (let iter = 0; iter < MAX_ITERS; iter += 1) {
        let bestDelta = -MIN_IMPROVEMENT;
        let bestAction = null;
        // Her iterasyonda hakim listesini karıştır: eşit iyileştirmeli swap'larda
        // hep aynı hakim çiftini seçen deterministic bias'ı (hakim isminin
        // baş harfine göre algılanan eğilim) kırar.
        const karisikHakimler = shuffle(aktifHakimler);

        for (let i = 0; i < karisikHakimler.length; i += 1) {
            const h1 = karisikHakimler[i];
            const h1Puan = hakimPuanlari.get(h1.id) ?? 0;
            const h1Adet = hakimDosyaSayilari.get(h1.id) ?? 0;
            const h1HedefPuan = hakimHedefPuanlari.get(h1.id) ?? 0;
            const h1HedefAdet = hakimHedefDosyaSayilari.get(h1.id) ?? 0;
            const h1Files = hakimDosyalari.get(h1.id);
            const h1Cost = costFor(h1.id);

            for (let j = 0; j < karisikHakimler.length; j += 1) {
                if (i === j) continue;
                const h2 = karisikHakimler[j];
                const h2Puan = hakimPuanlari.get(h2.id) ?? 0;
                const h2Adet = hakimDosyaSayilari.get(h2.id) ?? 0;
                const h2HedefPuan = hakimHedefPuanlari.get(h2.id) ?? 0;
                const h2HedefAdet = hakimHedefDosyaSayilari.get(h2.id) ?? 0;
                const h2Files = hakimDosyalari.get(h2.id);
                const h2Cost = costFor(h2.id);

                const baseCost = h1Cost + h2Cost;

                // 1-way move: h1 → h2
                for (let fi = 0; fi < h1Files.length; fi += 1) {
                    const f = h1Files[fi];
                    if (pinnedIds.has(f.id)) continue;
                    const p = Number(f.dosyaPuani) || 0;
                    const newH1 = quadDevCost(h1Puan - p, h1Adet - 1, h1HedefPuan, h1HedefAdet, puanW, adetW, cesitlilikW);
                    const newH2 = quadDevCost(h2Puan + p, h2Adet + 1, h2HedefPuan, h2HedefAdet, puanW, adetW, cesitlilikW);
                    const delta = (newH1 + newH2) - baseCost;
                    if (delta < bestDelta) {
                        bestDelta = delta;
                        bestAction = { type: 'move', h1Id: h1.id, h2Id: h2.id, fIdx: fi };
                    }
                }

                // 2-way swap — i < j ile tekrar saymayı önle
                if (i < j) {
                    for (let f1i = 0; f1i < h1Files.length; f1i += 1) {
                        const f1 = h1Files[f1i];
                        if (pinnedIds.has(f1.id)) continue;
                        const p1 = Number(f1.dosyaPuani) || 0;
                        for (let f2i = 0; f2i < h2Files.length; f2i += 1) {
                            const f2 = h2Files[f2i];
                            if (pinnedIds.has(f2.id)) continue;
                            const p2 = Number(f2.dosyaPuani) || 0;
                            if (p1 === p2) continue; // aynı puan takası etkisiz
                            const newH1 = quadDevCost(h1Puan - p1 + p2, h1Adet, h1HedefPuan, h1HedefAdet, puanW, adetW, cesitlilikW);
                            const newH2 = quadDevCost(h2Puan - p2 + p1, h2Adet, h2HedefPuan, h2HedefAdet, puanW, adetW, cesitlilikW);
                            const delta = (newH1 + newH2) - baseCost;
                            if (delta < bestDelta) {
                                bestDelta = delta;
                                bestAction = { type: 'swap', h1Id: h1.id, h2Id: h2.id, f1Idx: f1i, f2Idx: f2i };
                            }
                        }
                    }
                }
            }
        }

        if (!bestAction) break;

        if (bestAction.type === 'move') {
            const { h1Id, h2Id, fIdx } = bestAction;
            const h1Files = hakimDosyalari.get(h1Id);
            const [f] = h1Files.splice(fIdx, 1);
            const p = Number(f.dosyaPuani) || 0;
            hakimPuanlari.set(h1Id, (hakimPuanlari.get(h1Id) ?? 0) - p);
            hakimDosyaSayilari.set(h1Id, (hakimDosyaSayilari.get(h1Id) ?? 0) - 1);
            const h2 = hakimById.get(h2Id);
            hakimDosyalari.get(h2Id).push({ ...f, atananHakim: h2.name });
            hakimPuanlari.set(h2Id, (hakimPuanlari.get(h2Id) ?? 0) + p);
            hakimDosyaSayilari.set(h2Id, (hakimDosyaSayilari.get(h2Id) ?? 0) + 1);
        } else {
            const { h1Id, h2Id, f1Idx, f2Idx } = bestAction;
            const h1Files = hakimDosyalari.get(h1Id);
            const h2Files = hakimDosyalari.get(h2Id);
            const h1 = hakimById.get(h1Id);
            const h2 = hakimById.get(h2Id);
            const f1 = h1Files[f1Idx];
            const f2 = h2Files[f2Idx];
            const p1 = Number(f1.dosyaPuani) || 0;
            const p2 = Number(f2.dosyaPuani) || 0;
            h1Files[f1Idx] = { ...f2, atananHakim: h1.name };
            h2Files[f2Idx] = { ...f1, atananHakim: h2.name };
            hakimPuanlari.set(h1Id, (hakimPuanlari.get(h1Id) ?? 0) - p1 + p2);
            hakimPuanlari.set(h2Id, (hakimPuanlari.get(h2Id) ?? 0) - p2 + p1);
        }
    }
};

const calculateDengesizlikOrani = (mevcutPuanlar, hedefPuanlar) => {
    let toplamSapma = 0;
    let toplamHedefPuan = 0;

    for (const [hakimId, mevcutPuan] of mevcutPuanlar) {
        const hedefPuan = hedefPuanlar.get(hakimId);
        toplamSapma += Math.abs(mevcutPuan - hedefPuan);
        toplamHedefPuan += hedefPuan;
    }

    return toplamHedefPuan > EPS ? toplamSapma / toplamHedefPuan : 0;
};

const parseAssignmentRatio = (ratio) => {
    if (typeof ratio === 'number') return ratio;
    if (typeof ratio === 'string') {
        if (ratio.includes('/')) {
            const [numerator, denominator] = ratio.split('/').map(Number);
            if (denominator !== 0) {
                return numerator / denominator;
            }
        }
        const parsed = parseFloat(ratio);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }
    console.warn(`Geçersiz tevzi oranı: ${ratio}, varsayılan değer 1 kullanılıyor.`);
    return 1;
};

const createEmptyResult = (dosyaSayisi) => ({
    dagitilmisDosyalar: [],
    yeniHakimPuanlari: {},
    hakimHedefPuanlari: {},
    hakimDosyaSayilari: {},
    toplamDosyaPuani: 0,
    ortalamaHakimPuani: 0,
    dengesizlikOrani: 1
});

const findHakimByName = (hakimAdi, hakimler) => {
    if (!hakimAdi) return null;
    const target = turkishToLower(String(hakimAdi).trim());
    if (!target) return null;
    return hakimler.find((h) => turkishToLower(String(h.name || '').trim()) === target);
};
